/**
 * Success page after a public camp registration Stripe checkout.
 * Verifies the session and tells the visitor what to expect next.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2, Home, AlertCircle } from 'lucide-react';
import Footer from '../../components/Footer';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

export default function PublicCampRegisterSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [state, setState] = useState({ loading: true, result: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, result: null, error: 'No session ID provided' });
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/camps/public/register-success/${sessionId}`
        );
        setState({ loading: false, result: data, error: null });
      } catch (e) {
        setState({
          loading: false,
          result: null,
          error: e.response?.data?.detail || 'Could not verify payment',
        });
      }
    })();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="camp-register-success-page">
      <main className="flex-1 max-w-xl mx-auto px-4 py-12 w-full">
        <Card>
          <CardContent className="py-12 text-center">
            {state.loading ? (
              <>
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-emerald-500" />
                <p className="text-slate-600 mt-4">Confirming your payment…</p>
              </>
            ) : state.error ? (
              <>
                <AlertCircle className="h-14 w-14 mx-auto text-red-500" />
                <h1 className="text-2xl font-bold mt-4">Something went wrong</h1>
                <p className="text-slate-600 mt-2">{state.error}</p>
                <Button onClick={() => navigate('/')} className="mt-6" data-testid="go-home-btn">
                  <Home className="h-4 w-4 mr-2" /> Return Home
                </Button>
              </>
            ) : state.result?.status === 'success' ? (
              <>
                <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-600" />
                <h1 className="text-3xl font-bold mt-4">Registration Confirmed!</h1>
                <p className="text-slate-600 mt-3">
                  <strong>{state.result.player_name}</strong> is registered for{' '}
                  <strong>{state.result.camp_name}</strong>.
                </p>
                {state.result.is_new_account && (
                  <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-left">
                    <p className="text-sm font-medium text-emerald-800">
                      📧 Check your email at <strong>{state.result.parent_email}</strong>
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      We've sent your login details and a temporary password. You can manage
                      this registration (and register more kids or camps) from your parent
                      dashboard.
                    </p>
                  </div>
                )}
                {state.result.payment_status === 'deposit_paid' && (
                  <p className="text-sm text-slate-500 mt-4">
                    Deposit received. You can pay the remaining balance anytime from your
                    parent account.
                  </p>
                )}
                <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                  <Button onClick={() => navigate('/')} variant="outline">
                    <Home className="h-4 w-4 mr-2" /> Home
                  </Button>
                  <Button
                    onClick={() => navigate('/?tab=login')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="go-login-btn"
                  >
                    Log in to Your Account
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-amber-500" />
                <h1 className="text-xl font-semibold mt-4">Payment Pending</h1>
                <p className="text-slate-600 mt-2">
                  Your payment is still processing. You'll receive a confirmation email
                  shortly.
                </p>
                <Button onClick={() => navigate('/')} className="mt-6">
                  <Home className="h-4 w-4 mr-2" /> Home
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
